import { useMemo } from 'react';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { getFeatures, hasFeature, type FeatureFlags, type LicenseType } from '../config/features';

/**
 * Hook para verificar acceso a features según la licencia del grupo activo
 * 
 * @example
 * const { canAccessGrades, canAccessTemplates, features } = useFeatureAccess();
 * 
 * if (canAccessGrades) {
 *   // Mostrar módulo de calificaciones
 * }
 */
export function useFeatureAccess() {
  const { activeFamily } = useActiveFamily();
  
  const licenseType: LicenseType = useMemo(() => {
    return (activeFamily?.licenseType as LicenseType) || 'familia';
  }, [activeFamily?.licenseType]);
  
  const features: FeatureFlags = useMemo(() => {
    return getFeatures(licenseType);
  }, [licenseType]);
  
  return {
    // Tipo de licencia actual
    licenseType,
    
    // Objeto completo de features
    features,
    
    // Accesos rápidos a features específicas - Familia
    canAccessTemplates: features.templates,
    canAccessMarketplace: features.marketplace,
    canAccessFamilyStats: features.familyStats,
    canAccessChallenges: features.challenges,
    
    // Accesos rápidos a features específicas - Educación
    canAccessAttendance: features.attendance,
    canAccessGrades: features.grades,
    canAccessEvaluations: features.evaluations,
    canAccessReports: features.reports,
    canAccessResources: features.resources,
    canAccessBulletin: features.bulletin,
    canAccessSubjects: features.subjects,
    canAccessGradePeriods: features.gradePeriods,
    
    // Helpers
    isEducacion: licenseType === 'educacion',
    isFamilia: licenseType === 'familia',
    
    // Método genérico para verificar cualquier feature
    hasFeature: (featureName: keyof FeatureFlags) => hasFeature(licenseType, featureName),
  };
}
